import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIVictoriaSection = () => {
  const [activeCommand, setActiveCommand] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const commands = [
    {
      query: 'Покажи горячие лиды за последнюю неделю',
      response: 'Найдено 47 горячих лидов с оценкой >85%. Топ-3:\n1. ООО "ТехноПром" - 94% (бюджет 2.5М₽)\n2. ИП Соколов - 91% (готов к встрече)\n3. АО "МегаСтрой" - 89% (повторное обращение)',
      confidence: 94,
      action: 'Создать задачи менеджерам',
      icon: 'Flame'
    },
    {
      query: 'Автоматизируй отправку коммерческих предложений',
      response: 'Создан workflow:\n1. Триггер: новый лид с оценкой >70%\n2. Генерация КП на основе шаблона\n3. Персонализация под отрасль клиента\n4. Отправка на email + уведомление в Telegram\n5. Отслеживание открытий',
      confidence: 98,
      action: 'Активировать workflow',
      icon: 'Zap'
    },
    {
      query: 'Проанализируй причины потери сделок в Q4',
      response: 'Анализ 156 проигранных сделок:\n• 42% - высокая цена (средний чек 850К₽)\n• 28% - долгое время ответа (>48ч)\n• 18% - отсутствие нужной функции\n• 12% - выбрали конкурента\n\nРекомендация: снизить время ответа до 24ч',
      confidence: 87,
      action: 'Создать план улучшений',
      icon: 'BarChart3'
    }
  ];

  const handleCommandClick = (index) => {
    setActiveCommand(index);
    setIsTyping(true);
    setTimeout(() => setIsTyping(false), 1500);
  };

  return (
    <section ref={sectionRef} className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-card via-background to-card" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 glassmorphism px-4 py-2 rounded-full mb-6">
            <Icon name="Sparkles" size={16} color="var(--color-secondary)" />
            <span className="text-sm font-medium text-secondary">Искусственный интеллект</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl font-black text-foreground mb-4">
            Познакомьтесь с <span className="bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent">Викторией</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            ИИ-ассистент, который понимает ваш бизнес и автоматизирует рутину
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {commands?.map((command, index) => (
            <div
              key={index}
              onClick={() => handleCommandClick(index)}
              className={`glassmorphism rounded-xl p-6 cursor-pointer transition-all duration-500 hover:scale-105 ${
                activeCommand === index ? 'border-2 border-secondary neon-glow-accent' : ''
              } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={command?.icon} size={20} color="var(--color-secondary)" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {command?.query}
                  </p>
                </div>
              </div>
              
              {activeCommand === index && (
                <div className="flex items-center space-x-2 animate-fade-in">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                  <span className="text-xs text-success font-medium">Активная команда</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={`glassmorphism-strong rounded-2xl p-8 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center flex-shrink-0">
              <Icon name="Bot" size={24} color="var(--color-foreground)" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <h3 className="text-lg font-bold text-foreground">Виктория</h3>
                <div className="flex items-center space-x-1 px-2 py-0.5 rounded-full bg-success/10 border border-success/20">
                  <div className="w-1.5 h-1.5 bg-success rounded-full animate-pulse" />
                  <span className="text-xs text-success font-medium">Онлайн</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">ИИ-ассистент • Обучена на вашем бизнесе</p>
            </div>
          </div>

          <div className="glassmorphism rounded-xl p-6 mb-4">
            <div className="flex items-start space-x-3 mb-4">
              <Icon name="User" size={20} color="var(--color-muted-foreground)" />
              <p className="text-sm text-foreground flex-1">
                {commands?.[activeCommand]?.query}
              </p>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center flex-shrink-0">
                <Icon name="Bot" size={14} color="var(--color-foreground)" />
              </div>
              <div className="flex-1">
                {isTyping ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                ) : (
                  <div className="space-y-4 animate-fade-in">
                    <p className="text-sm text-foreground whitespace-pre-line">
                      {commands?.[activeCommand]?.response}
                    </p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <Icon name="Target" size={16} color="var(--color-success)" />
                          <span className="text-xs text-muted-foreground">
                            Уверенность: <span className="text-success font-bold">{commands?.[activeCommand]?.confidence}%</span>
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Clock" size={16} color="var(--color-accent)" />
                          <span className="text-xs text-muted-foreground">
                            Ответ за <span className="text-accent font-bold">1.2с</span>
                          </span>
                        </div>
                      </div>
                      
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Play"
                        iconPosition="left"
                      >
                        {commands?.[activeCommand]?.action}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="glassmorphism rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-primary mb-1">24/7</div>
              <div className="text-xs text-muted-foreground">Работа без перерывов</div>
            </div>
            <div className="glassmorphism rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-accent mb-1">10x</div>
              <div className="text-xs text-muted-foreground">Быстрее человека</div>
            </div>
            <div className="glassmorphism rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-secondary mb-1">99.8%</div>
              <div className="text-xs text-muted-foreground">Точность анализа</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIVictoriaSection;